/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { type ReactElement } from "react";

import { css, jsx } from "@compiled/react";

import { Field } from "@atlaskit/form";
import TextField from "@atlaskit/textfield";
import { N500, N900, R400 } from "@atlaskit/theme/colors";
import { token } from "@atlaskit/tokens";

const fieldLabelStyles = css({
    color: token("color.text.subtle", N500),
    fontFamily: token("font.family.brand.body"),
    fontStyle: "normal",
    fontWeight: token("font.weight.medium"),
    fontSize: "14px",
    lineHeight: "15px"
});

const disabledInputColor = token("color.text.accent.gray.bolder", N900);
const disabledSiteNameInputStyles = css({
    "&[data-ds--text-field--container]": {
        border: "2px solid"
    },
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
    "& input[disabled]": {
        WebkitTextFillColor: disabledInputColor,
        color: disabledInputColor
    }
});

const errorMessageStyles = css({
    color: token("color.text.danger", R400),

    fontFamily: token("font.family.brand.body"),
    fontWeight: token("font.weight.regular"),
    fontSize: "12px",
    lineHeight: "12px",
    letterSpacing: "0",
    marginBlockStart: token("space.050", "4px")
});

export interface DisabledSiteNameInputProps {
    value: string;
    fieldName: string;
    fieldLabel?: string;
    readonly renderPreview?: (value: string) => ReactElement;
}

export const DisabledSiteNameInput = ({
    fieldName,
    fieldLabel,
    renderPreview,
    value: defaultValue
}: DisabledSiteNameInputProps) => {
    return (
        <Field
            name={fieldName}
            defaultValue={defaultValue}
            label={<span css={fieldLabelStyles}>{fieldLabel}</span>}
        >
            {({ fieldProps, error }) => {
                const urlPreview = fieldProps.value
                    ? `${fieldProps.value}`
                    : "your-company-name.atlassian.net";

                return (
                    <div>
                        <TextField
                            {...fieldProps}
                            isDisabled
                            css={disabledSiteNameInputStyles}
                        />
                        {error && <span css={errorMessageStyles}>{error}</span>}
                        {renderPreview && renderPreview(urlPreview)}
                    </div>
                );
            }}
        </Field>
    );
};
